// File: frontend/src/pages/ProfilePage.tsx
// Updated to include ApiKeysSection component

import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import { useApi } from '../hooks/useApi';
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { 
    Box, 
    Typography, 
    TextField, 
    Button, 
    CircularProgress, 
    Alert, 
    Paper, 
    Stack,
    FormControlLabel,
    Switch,
    alpha
} from '@mui/material';
import ApiKeysSection from '../components/ApiKeys/ApiKeysSection';
import { cyber } from '../theme';

interface UserProfile {
    email?: string;
    displayName?: string;
    jobTitle?: string;
    lastLoginDate: string;
    newsletter_subscribed: boolean;
}

const ProfilePage = () => {
    const { inProgress } = useMsal();
    const { authenticatedFetcher, poster } = useApi();

    const userProfileApiEndpoint = (inProgress === InteractionStatus.None) ? '/api/users/me' : null;

    const { data: profile, error, isLoading, mutate } = useSWR<UserProfile>(
        userProfileApiEndpoint, 
        authenticatedFetcher
    );

    // State for all editable fields
    const [displayName, setDisplayName] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [newsletterSubscribed, setNewsletterSubscribed] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

    // When the profile data is loaded, populate the local state
    useEffect(() => {
        if (profile) {
            setDisplayName(profile.displayName || '');
            setJobTitle(profile.jobTitle || '');
            setNewsletterSubscribed(profile.newsletter_subscribed || false);
        }
    }, [profile]);

    // A single save handler for all changes
    const handleSave = async () => {
        setIsSaving(true);
        setSaveMessage(null);
        try {
            await poster('/api/users/me', { 
                displayName, 
                jobTitle,
                newsletter_subscribed: newsletterSubscribed 
            }, 'PUT');
            
            mutate(); 
            setSaveMessage({ type: 'success', text: 'Profile updated successfully!' });
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
            setSaveMessage({ type: 'error', text: `Failed to update profile: ${errorMessage}` });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading || inProgress !== InteractionStatus.None) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
    }
    
    if (error) {
        return <Alert severity="error">Failed to load user profile. Please try refreshing the page. Error: {error.message}</Alert>;
    }

    return (
        <Paper sx={{ p: 4, maxWidth: '900px', mx: 'auto', mt: 4, border: `1px solid ${cyber.border}` }}>
            <Typography variant="h4" component="h1" gutterBottom>My Profile</Typography>
            <Stack spacing={3}>
                <TextField
                    fullWidth
                    label="Email Address"
                    value={profile?.email || ''}
                    disabled
                    variant="filled"
                    sx={{
                        '& .MuiFilledInput-input': { fontFamily: cyber.fontMono },
                        '& .MuiFilledInput-input.Mui-disabled': {
                            WebkitTextFillColor: alpha(cyber.accent.cyan, 0.85),
                        },
                    }}
                />
                
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                    <TextField fullWidth label="Display Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                    <TextField fullWidth label="Job Title" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
                </Stack>
                
                <Typography variant="body2" color="text.secondary">
                    <strong>Last Login:</strong> {profile ? new Date(profile.lastLoginDate).toLocaleString() : 'N/A'}
                </Typography>

                <FormControlLabel
                    control={
                        <Switch
                            checked={newsletterSubscribed}
                            onChange={(e) => setNewsletterSubscribed(e.target.checked)}
                        />
                    }
                    label="Subscribe to weekly newsletter"
                />

                <Box sx={{ textAlign: 'right' }}>
                    <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                </Box>

                {saveMessage && (
                    <Alert severity={saveMessage.type} onClose={() => setSaveMessage(null)}>{saveMessage.text}</Alert>
                )}

                {/* API Keys Section */}
                <ApiKeysSection />

            </Stack>
        </Paper>
    );
};

export default ProfilePage;